import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Rocket, Landmark, Building2, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/ui/section-header";
import { Price } from "@/components/pricing/Price";
import { PRICING } from "@/config/pricing";
import { Reveal } from "@/components/motion/Reveal";
import { StaggerGrid } from "@/components/motion/StaggerGrid";
import { HoverLift } from "@/components/motion/HoverLift";

export default function Teams() {
  const { t } = useTranslation();

  const includes = [
    t("marketing.teams.inc1"),
    t("marketing.teams.inc2"),
    t("marketing.teams.inc3"),
    t("marketing.teams.inc4"),
    t("marketing.teams.inc5"),
    t("marketing.teams.inc6"),
  ];

  const operators = [
    { name: "PayLink NG", city: "Lagos, Nigeria" },
    { name: "Senlogi",    city: "Lomé, Togo" },
    { name: "Folha Digital", city: "São Paulo, Brasil" },
  ];

  const audiences = [
    {
      Icon: Rocket,
      title: t("marketing.teams.aud1Title"),
      desc:  t("marketing.teams.aud1Desc"),
    },
    {
      Icon: Landmark,
      title: t("marketing.teams.aud2Title"),
      desc:  t("marketing.teams.aud2Desc"),
    },
    {
      Icon: Building2,
      title: t("marketing.teams.aud3Title"),
      desc:  t("marketing.teams.aud3Desc"),
    },
  ];

  return (
    <PublicShell>
      <Seo
        title={t("marketing.teams.seoTitle")}
        description={t("marketing.teams.seoDescription")}
      />

      {/* Hero */}
      <section className="border-b border-border">
        <Reveal className="container py-20 md:py-24">
          <SectionHeader
            align="center"
            titleAs="h1"
            eyebrow={t("marketing.teams.eyebrow")}
            title={t("marketing.teams.h1")}
            lede={t("marketing.teams.sub")}
          />
          <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="brand">
              <Link to="/contact?source=teams">
                {t("marketing.teams.ctaPrimary")} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </section>

      {/* Pricing card */}
      <section className="border-b border-border bg-gradient-to-br from-primary/10 to-background">
        <Reveal className="container py-16">
          <Card className="mx-auto max-w-4xl border-primary/40">
            <CardContent className="grid gap-8 p-8 md:grid-cols-[1.2fr_1fr] md:items-center">
              <div>
                <h2 className="t-display-sm">
                  {t("marketing.teams.pricingTitle")}
                </h2>
                <ul className="mt-5 grid gap-2 text-sm sm:grid-cols-2">
                  {includes.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.25} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-start gap-4 md:items-end">
                <Price
                  size="hero"
                  eurCents={PRICING.team.seat_monthly_eur_cents}
                  suffix={t("marketing.teams.perSeatMonth")}
                />
                <p className="text-sm text-muted-foreground md:text-right">
                  {t("marketing.teams.minSeats", { n: PRICING.team.min_seats })}
                </p>
                <Button asChild size="lg" variant="brand">
                  <Link to="/contact?source=teams">
                    {t("marketing.teams.ctaPrimary")} →
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </Reveal>
      </section>

      {/* Operator brand bar */}
      <section className="border-b border-border">
        <Reveal className="container py-12">
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("marketing.teams.trustedBy")}
          </p>
          <StaggerGrid className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {operators.map((o) => (
              <HoverLift key={o.name} className="rounded-lg border border-border bg-card px-6 py-5 text-center">
                <div className="text-lg font-semibold">{o.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">{o.city}</div>
              </HoverLift>
            ))}
          </StaggerGrid>
        </Reveal>
      </section>

      {/* Who it's for */}
      <section className="border-b border-border bg-muted/20">
        <Reveal className="container py-16">
          <h2 className="text-center t-display-md">
            {t("marketing.teams.whoTitle")}
          </h2>
          <StaggerGrid className="mt-10 grid gap-6 md:grid-cols-3">
            {audiences.map(({ Icon, title, desc }) => (
              <HoverLift key={title} className="rounded-xl h-full">
              <Card className="h-full">
                <CardHeader>
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="mt-3 text-xl">{title}</CardTitle>
                  <CardDescription className="mt-2">{desc}</CardDescription>
                </CardHeader>
              </Card>
              </HoverLift>
            ))}
          </StaggerGrid>
        </Reveal>
      </section>

      {/* Closing CTA */}
      <section>
        <Reveal className="container py-20 text-center">
          <h2 className="t-display-md">
            {t("marketing.teams.closingTitle")}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {t("marketing.teams.closingSub")}
          </p>
          <Button asChild size="lg" variant="brand" className="mt-6">
            <Link to="/contact?source=teams">
              {t("marketing.teams.ctaPrimary")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </Reveal>
      </section>
    </PublicShell>
  );
}
